import { ChangeDetectionStrategy, Component, Input, OnInit } from '@angular/core';
import { ControlValueAccessor, FormGroupDirective, NG_VALUE_ACCESSOR, UntypedFormControl } from '@angular/forms';
import { LedgerAccount } from '@app/modules/statements/models/statements.models';
import { StatementsAccountingIntegrationService } from '@app/modules/statements/services/statements-accounting-integration.service';
import { debounceTime, map, shareReplay } from 'rxjs/operators';
import { startWith } from 'rxjs/operators';

@Component({
  selector: 'sbnb-statements-accounting-connection-form-account-dropdown',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: StatementsAccountingConnectionFormAccountDropdownComponent,
    },
  ],
  template: `
    <mat-form-field class="max-w-[300px] w-[300px] nopadding nomargin input__mini" data-cy="account-dropdown">
      <mat-select [value]="selection" (selectionChange)="selectionChange($event.value)">
        <mat-select-trigger>
          <ng-container *ngIf="(defaultValue$ | async) === selection || !selection">
            <ng-container *ngTemplateOutlet="defaultLabel"></ng-container>
          </ng-container>
          <span>{{ selection === '' ? accountName : getSelectedAccount(selection)?.name }}</span>
        </mat-select-trigger>
        <mat-option>
          <ngx-mat-select-search
            [formControl]="availableAccountsSearchCtrl"
            placeholderLabel="Find account..."
            noEntriesFoundLabel="No matching account found"></ngx-mat-select-search>
        </mat-option>
        <mat-option *ngIf="(defaultValue$ | async) === null" value="">
          <div>
            <ng-container *ngTemplateOutlet="defaultLabel"></ng-container>
            {{ accountName }}
          </div>
        </mat-option>
        <mat-option *ngFor="let option of filteredAvailableAccounts$ | async" [value]="option.id">
          <ng-container *ngIf="(defaultValue$ | async) === option.id">
            <ng-container *ngTemplateOutlet="defaultLabel"></ng-container>
          </ng-container>
          {{ option.name }}
        </mat-option>
      </mat-select>
    </mat-form-field>
    <ng-template #defaultLabel>
      <span class="!text-night-lighter px-2 py-1 rounded-2xl bg-smoke-lighter text__xsmall">Default</span>
    </ng-template>
  `,
  styles: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StatementsAccountingConnectionFormAccountDropdownComponent implements ControlValueAccessor, OnInit {
  @Input() accountClass: string;
  @Input() accountName: string;
  @Input() accountReference: string;
  @Input() availableAccounts;
  public touched = false;
  public disabled = false;
  public selection: number | string = '';

  public availableAccountsSearchCtrl = new UntypedFormControl();
  public filteredAvailableAccounts$ = this.availableAccountsSearchCtrl.valueChanges.pipe(
    startWith(''),
    debounceTime(300),
    map((value) =>
      this.availableAccounts?.filter(
        (account) =>
          !value || account.name.toLowerCase().replace(/[-_]/g, ' ').includes(value.toLowerCase().replace(/[-_]/g, ' '))
      )
    )
  );

  public defaultValue$ = this.statementsAccountingIntegrationService.formState$.pipe(
    map((state) => {
      const fieldData = state.data?.accounts.fieldMap[this.accountReference];
      return fieldData.isDefault && fieldData.value ? fieldData.value : null;
    }),
    shareReplay(1)
  );

  constructor(
    public form: FormGroupDirective,
    private statementsAccountingIntegrationService: StatementsAccountingIntegrationService
  ) {}

  ngOnInit(): void {}

  /** ControlValueAccessor */
  onChange(selected: number | string) {}

  onTouched = () => {};

  writeValue(selected: number | string) {
    this.selection = selected;
  }

  registerOnChange(onChange: any) {
    this.onChange = onChange;
  }

  registerOnTouched(onTouched: any) {
    this.onTouched = onTouched;
  }

  markAsTouched() {
    if (!this.touched) {
      this.onTouched();
      this.touched = true;
    }
  }

  setDisabledState(disabled: boolean) {
    this.disabled = disabled;
  }
  /** End ControlValueAccessor */

  selectionChange(event: number | string) {
    this.selection = event;
    this.markAsTouched();
    this.onChange(event);
  }

  getSelectedAccount(id: number | string): LedgerAccount {
    return this.availableAccounts.find((account) => account.id === id);
  }
}
